/**
 * AcceptanceKPICard - AI-powered Acceptance Criteria and KPI suggestions
 * Part of the ScopeOps layer for Demand capture
 * 
 * Features:
 * - AI suggestions for acceptance criteria (3-7 testable, ≤140 chars)
 * - AI suggestions for KPIs (2-4 with name, unit, target, measurement)
 * - Accept all / Edit / Discard / Add another actions
 * - Tier-gated access (Basic: read-only + 1 baseline)
 */

import React, { useState, useCallback, useEffect } from 'react';
import { 
  CheckCircle, Target, Sparkles, ChevronDown, ChevronUp, 
  Check, X, Edit2, Save, RefreshCw, AlertTriangle, Plus,
  Loader2, CheckCheck, Trash2, Info
} from 'lucide-react';
import { api } from '../api/client';
import { useAuth } from '../context/AuthContext';
import { useProductMode } from '../context/ProductModeContext';
import { trackEvent, TELEMETRY_EVENTS } from '../utils/telemetry';
import { toast } from 'sonner';

// Status pill colors
const STATUS_COLORS = {
  proposed: 'bg-gray-100 text-gray-600',
  accepted: 'bg-green-100 text-green-700',
  'at-risk': 'bg-orange-100 text-orange-700',
  met: 'bg-green-100 text-green-700',
  failed: 'bg-red-100 text-red-700',
  'not-met': 'bg-red-100 text-red-700',
};

// Type pill colors
const TYPE_COLORS = {
  functional: 'bg-blue-100 text-blue-700',
  'non-functional': 'bg-purple-100 text-purple-700',
  ops: 'bg-teal-100 text-teal-700',
};

/**
 * Individual Acceptance Criterion Item
 */
const AcceptanceItem = ({ item, onStatusChange, onRemove, canEdit, isSelected, onToggleSelect }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editedText, setEditedText] = useState(item.text);
  
  const handleSave = () => {
    if (editedText.trim() !== item.text) {
      onStatusChange(item.id, 'accepted', editedText.trim());
    }
    setIsEditing(false);
  };
  
  // Validate text length (max 140 chars as per spec)
  const isTextValid = editedText.trim().length <= 140 && editedText.trim().length > 0;
  
  return (
    <div 
      className={`group flex items-start gap-3 p-3 bg-white rounded-lg border transition-all ${
        isSelected ? 'border-purple-300 bg-purple-50/50' : 'border-gray-100 hover:border-gray-200'
      }`}
      data-testid={`acceptance-item-${item.id}`}
    >
      {/* Checkbox for bulk selection */}
      {canEdit && (
        <div className="flex-shrink-0 mt-0.5">
          <input
            type="checkbox"
            checked={isSelected}
            onChange={() => onToggleSelect?.(item.id)}
            className="w-4 h-4 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
            aria-label={`Select ${item.text.substring(0, 30)}`}
          />
        </div>
      )}
      
      {/* Status indicator */}
      <div className="flex-shrink-0 mt-0.5">
        <div className={`w-6 h-6 rounded-full flex items-center justify-center ${
          item.status === 'accepted' || item.status === 'met' ? 'bg-green-500 text-white' :
          item.status === 'at-risk' || item.status === 'failed' ? 'bg-orange-500 text-white' :
          'bg-gray-200 text-gray-500'
        }`}>
          {item.status === 'accepted' || item.status === 'met' ? (
            <Check className="w-4 h-4" />
          ) : (
            <span className="text-xs font-bold">{item.type?.charAt(0).toUpperCase()}</span>
          )}
        </div>
      </div>
      
      <div className="flex-1 min-w-0">
        {isEditing ? (
          <div className="space-y-2">
            <textarea
              value={editedText}
              onChange={(e) => setEditedText(e.target.value)}
              className={`w-full p-2 text-sm border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent ${
                !isTextValid ? 'border-red-300' : 'border-gray-200'
              }`}
              rows={2}
              maxLength={150}
              aria-label="Edit acceptance criterion"
            />
            <div className="flex items-center justify-between">
              <span className={`text-xs ${editedText.length > 140 ? 'text-red-500' : 'text-gray-400'}`}>
                {editedText.length}/140
              </span>
              <div className="flex gap-2">
                <button
                  onClick={handleSave}
                  disabled={!isTextValid}
                  className="px-2 py-1 text-xs bg-purple-600 text-white rounded hover:bg-purple-700 disabled:opacity-50"
                  data-testid="save-edit-btn"
                >
                  <Save className="w-3 h-3 inline mr-1" /> Save
                </button>
                <button
                  onClick={() => {
                    setEditedText(item.text);
                    setIsEditing(false);
                  }}
                  className="px-2 py-1 text-xs bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        ) : (
          <>
            <p className="text-sm text-gray-800 leading-relaxed">{item.text}</p>
            <div className="flex items-center gap-2 mt-2 flex-wrap">
              <span className={`px-2 py-0.5 text-[10px] font-medium rounded-full ${TYPE_COLORS[item.type] || TYPE_COLORS.functional}`}>
                {item.type}
              </span>
              <span className={`px-2 py-0.5 text-[10px] font-medium rounded-full ${STATUS_COLORS[item.status] || STATUS_COLORS.proposed}`}>
                {item.status}
              </span>
              {item.source === 'ai' && (
                <span className="px-2 py-0.5 text-[10px] font-medium rounded-full bg-violet-100 text-violet-700">
                  AI Suggested
                </span>
              )}
            </div>
          </>
        )}
      </div>
      
      {/* Action buttons - always visible on hover */}
      {canEdit && !isEditing && (
        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
          <button
            onClick={() => setIsEditing(true)}
            className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded"
            title="Edit"
            aria-label="Edit criterion"
          >
            <Edit2 className="w-4 h-4" />
          </button>
          {item.status === 'proposed' && (
            <button
              onClick={() => onStatusChange(item.id, 'accepted')}
              className="p-1.5 text-green-500 hover:text-green-600 hover:bg-green-50 rounded"
              title="Accept"
              aria-label="Accept criterion"
            >
              <Check className="w-4 h-4" />
            </button>
          )}
          <button
            onClick={() => onRemove(item.id)}
            className="p-1.5 text-red-400 hover:text-red-500 hover:bg-red-50 rounded"
            title="Remove"
            aria-label="Remove criterion"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      )}
    </div>
  );
};

/**
 * Individual KPI Item
 */
const KPIItem = ({ item, onStatusChange, onRemove, canEdit, isSelected, onToggleSelect }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editedKPI, setEditedKPI] = useState(item);
  
  const handleSave = () => {
    onStatusChange(item.id, 'accepted', null, editedKPI);
    setIsEditing(false);
  };
  
  return (
    <div 
      className={`group p-3 bg-white rounded-lg border transition-all ${
        isSelected ? 'border-purple-300 bg-purple-50/50' : 'border-gray-100 hover:border-gray-200'
      }`}
      data-testid={`kpi-item-${item.id}`}
    >
      {isEditing ? (
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Name</label>
              <input
                type="text"
                value={editedKPI.name}
                onChange={(e) => setEditedKPI({ ...editedKPI, name: e.target.value })}
                className="w-full px-2 py-1.5 text-sm border border-gray-200 rounded focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Target</label>
                <input
                  type="text"
                  value={editedKPI.target || ''}
                  onChange={(e) => setEditedKPI({ ...editedKPI, target: e.target.value })}
                  className="w-full px-2 py-1.5 text-sm border border-gray-200 rounded focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Unit</label>
                <select
                  value={editedKPI.unit}
                  onChange={(e) => setEditedKPI({ ...editedKPI, unit: e.target.value })}
                  className="w-full px-2 py-1.5 text-sm border border-gray-200 rounded focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                >
                  <option value="%">%</option>
                  <option value="#">#</option>
                  <option value="s">seconds</option>
                  <option value="$">$</option>
                  <option value="qual">Qualitative</option>
                </select>
              </div>
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Measurement Method</label>
            <input
              type="text"
              value={editedKPI.measurementMethod || ''}
              onChange={(e) => setEditedKPI({ ...editedKPI, measurementMethod: e.target.value })}
              className="w-full px-2 py-1.5 text-sm border border-gray-200 rounded focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              placeholder="How will this KPI be measured?"
            />
          </div>
          <div className="flex gap-2 justify-end">
            <button
              onClick={() => {
                setEditedKPI(item);
                setIsEditing(false);
              }}
              className="px-3 py-1.5 text-xs text-gray-600 hover:bg-gray-100 rounded"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              className="px-3 py-1.5 text-xs bg-purple-600 text-white rounded hover:bg-purple-700"
            >
              Save
            </button>
          </div>
        </div>
      ) : (
        <>
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-3">
              {canEdit && (
                <input
                  type="checkbox"
                  checked={isSelected}
                  onChange={() => onToggleSelect?.(item.id)}
                  className="mt-1 w-4 h-4 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                  aria-label={`Select ${item.name}`}
                />
              )}
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <Target className="w-4 h-4 text-purple-500" />
                  <h4 className="font-medium text-sm text-gray-800">{item.name}</h4>
                </div>
                <div className="flex items-center gap-3 text-xs text-gray-500">
                  <span>Target: <strong className="text-gray-700">{item.target} {item.unit}</strong></span>
                  {item.baseline && <span>Baseline: {item.baseline}</span>}
                </div>
                {item.measurementMethod && (
                  <p className="mt-2 text-xs text-gray-500 line-clamp-2">{item.measurementMethod}</p>
                )}
              </div>
            </div>
            
            {canEdit && (
              <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                <button
                  onClick={() => setIsEditing(true)}
                  className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded"
                  title="Edit"
                >
                  <Edit2 className="w-4 h-4" />
                </button>
                {item.status === 'proposed' && (
                  <button
                    onClick={() => onStatusChange(item.id, 'accepted')}
                    className="p-1.5 text-green-500 hover:text-green-600 hover:bg-green-50 rounded"
                    title="Accept"
                  >
                    <Check className="w-4 h-4" />
                  </button>
                )}
                <button
                  onClick={() => onRemove(item.id)}
                  className="p-1.5 text-red-400 hover:text-red-500 hover:bg-red-50 rounded"
                  title="Remove"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            )}
          </div>
          
          <div className="flex items-center gap-2 mt-2">
            <span className={`px-2 py-0.5 text-[10px] font-medium rounded-full ${STATUS_COLORS[item.status] || STATUS_COLORS.proposed}`}>
              {item.status}
            </span>
            {item.source === 'ai' && (
              <span className="px-2 py-0.5 text-[10px] font-medium rounded-full bg-violet-100 text-violet-700">
                AI Suggested
              </span>
            )}
          </div>
        </>
      )}
    </div>
  );
};

/**
 * Main AcceptanceKPICard Component
 */
const AcceptanceKPICard = ({ 
  projectId, 
  demandSummary,
  initialAcceptance = [],
  initialKPIs = [],
  onSave,
  readOnly = false,
  variant = 'full', // 'full' | 'compact' | 'step' (for wizard)
  className = ''
}) => {
  const { user } = useAuth();
  const { canEditGovernance, hasBenefitsKpis, scopingTier, isBasicTier } = useProductMode();
  
  const tenantId = user?.tenant?.id || user?.tenant_id;
  
  const [activeTab, setActiveTab] = useState('acceptance');
  const [acceptance, setAcceptance] = useState(initialAcceptance);
  const [kpis, setKPIs] = useState(initialKPIs);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [expanded, setExpanded] = useState(variant !== 'compact');
  const [hasChanges, setHasChanges] = useState(false);
  const [selectedAcceptance, setSelectedAcceptance] = useState(new Set());
  const [selectedKPIs, setSelectedKPIs] = useState(new Set());
  const [showAddNew, setShowAddNew] = useState(false);
  const [newItemText, setNewItemText] = useState('');
  const [newItemType, setNewItemType] = useState('functional');
  
  // Determine edit permissions
  const canEdit = !readOnly && canEditGovernance !== false;
  const canSuggestKPIs = hasBenefitsKpis !== false;
  
  // Sync with initial data
  useEffect(() => {
    setAcceptance(initialAcceptance);
    setKPIs(initialKPIs);
  }, [initialAcceptance, initialKPIs]);
  
  // Generate AI acceptance suggestions
  const generateAcceptanceSuggestions = useCallback(async () => {
    if (!demandSummary || !tenantId) {
      toast.error('Demand summary required for AI suggestions');
      return;
    }
    
    setGenerating(true);
    trackEvent(TELEMETRY_EVENTS.AI_ACCEPTANCE_SUGGEST_REQUESTED, { projectId });
    
    try {
      const response = await api.suggestAcceptanceCriteria(tenantId, {
        demandSummary
      });
      
      const newCriteria = response.data.acceptanceCriteria || [];
      setAcceptance(prev => [...prev, ...newCriteria]);
      setHasChanges(true);
      toast.success(`Generated ${newCriteria.length} acceptance criteria`);
    } catch (err) {
      console.error('Failed to generate acceptance:', err);
      toast.error(err.response?.data?.detail || 'Failed to generate suggestions');
    } finally {
      setGenerating(false);
    }
  }, [demandSummary, tenantId, projectId]);
  
  // Generate AI KPI suggestions
  const generateKPISuggestions = useCallback(async () => {
    if (!demandSummary || !tenantId) {
      toast.error('Demand summary required for AI suggestions');
      return;
    }
    
    setGenerating(true);
    trackEvent(TELEMETRY_EVENTS.AI_KPI_SUGGEST_REQUESTED, { projectId });
    
    try {
      const response = await api.suggestKPIs(tenantId, {
        demandSummary,
        acceptanceCriteria: acceptance
      });
      
      const newKPIs = response.data.kpis || [];
      setKPIs(prev => [...prev, ...newKPIs]);
      setHasChanges(true);
      toast.success(`Generated ${newKPIs.length} KPIs`);
    } catch (err) {
      console.error('Failed to generate KPIs:', err);
      toast.error(err.response?.data?.detail || 'Failed to generate KPI suggestions');
    } finally {
      setGenerating(false);
    }
  }, [demandSummary, tenantId, projectId, acceptance]);
  
  // Handle acceptance status change
  const handleAcceptanceStatusChange = (id, newStatus, newText = null) => {
    setAcceptance(prev => prev.map(item => 
      item.id === id ? { ...item, status: newStatus, text: newText || item.text } : item
    ));
    setHasChanges(true);
  };
  
  // Handle KPI status change
  const handleKPIStatusChange = (id, newStatus, _, updatedKPI = null) => {
    setKPIs(prev => prev.map(item => 
      item.id === id ? (updatedKPI ? { ...updatedKPI, status: newStatus } : { ...item, status: newStatus }) : item
    ));
    setHasChanges(true);
  };
  
  // Remove acceptance
  const removeAcceptance = (id) => {
    setAcceptance(prev => prev.filter(item => item.id !== id));
    setSelectedAcceptance(prev => {
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
    setHasChanges(true);
  };
  
  // Remove KPI
  const removeKPI = (id) => {
    setKPIs(prev => prev.filter(item => item.id !== id));
    setSelectedKPIs(prev => {
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
    setHasChanges(true);
  };
  
  // Toggle selection
  const toggleAcceptanceSelect = (id) => {
    setSelectedAcceptance(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  
  const toggleKPISelect = (id) => {
    setSelectedKPIs(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  
  // Bulk accept all selected
  const acceptAllSelected = () => {
    if (activeTab === 'acceptance' && selectedAcceptance.size > 0) {
      setAcceptance(prev => prev.map(item => 
        selectedAcceptance.has(item.id) ? { ...item, status: 'accepted' } : item
      ));
      setSelectedAcceptance(new Set());
      setHasChanges(true);
      toast.success(`Accepted ${selectedAcceptance.size} criteria`);
    } else if (activeTab === 'kpis' && selectedKPIs.size > 0) {
      setKPIs(prev => prev.map(item => 
        selectedKPIs.has(item.id) ? { ...item, status: 'accepted' } : item
      ));
      setSelectedKPIs(new Set());
      setHasChanges(true);
      toast.success(`Accepted ${selectedKPIs.size} KPIs`);
    }
  };
  
  // Bulk discard selected
  const discardSelected = () => {
    if (activeTab === 'acceptance' && selectedAcceptance.size > 0) {
      setAcceptance(prev => prev.filter(item => !selectedAcceptance.has(item.id)));
      setSelectedAcceptance(new Set());
      setHasChanges(true);
      toast.success(`Removed ${selectedAcceptance.size} criteria`);
    } else if (activeTab === 'kpis' && selectedKPIs.size > 0) {
      setKPIs(prev => prev.filter(item => !selectedKPIs.has(item.id)));
      setSelectedKPIs(new Set());
      setHasChanges(true);
      toast.success(`Removed ${selectedKPIs.size} KPIs`);
    }
  };
  
  // Select all
  const selectAll = () => {
    if (activeTab === 'acceptance') {
      setSelectedAcceptance(new Set(acceptance.map(a => a.id)));
    } else {
      setSelectedKPIs(new Set(kpis.map(k => k.id)));
    }
  };
  
  // Add new item manually
  const addNewItem = () => {
    if (!newItemText.trim()) return;
    
    const newItem = {
      id: `manual_${Date.now()}`,
      text: newItemText.trim(),
      type: newItemType,
      source: 'user',
      status: 'accepted',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };
    
    setAcceptance(prev => [...prev, newItem]);
    setNewItemText('');
    setShowAddNew(false);
    setHasChanges(true);
    toast.success('Added new criterion');
  };
  
  // Save changes
  const handleSave = async () => {
    // If no projectId, save locally (wizard mode - just pass to parent)
    if (!projectId) {
      setHasChanges(false);
      toast.success('Changes saved to proposal');
      onSave?.({ acceptance, kpis });
      return;
    }
    
    if (!tenantId) {
      toast.error('Tenant ID required');
      return;
    }
    
    setLoading(true);
    
    try {
      // Save acceptance criteria
      await api.saveAcceptanceCriteria(projectId, tenantId, acceptance);
      trackEvent(TELEMETRY_EVENTS.AI_ACCEPTANCE_SAVED, { 
        projectId, 
        count: acceptance.length 
      });
      
      // Save KPIs
      await api.saveKPIs(projectId, tenantId, kpis);
      trackEvent(TELEMETRY_EVENTS.AI_KPI_SAVED, { 
        projectId, 
        count: kpis.length 
      });
      
      setHasChanges(false);
      toast.success('Saved successfully');
      onSave?.({ acceptance, kpis });
    } catch (err) {
      console.error('Failed to save:', err);
      toast.error('Failed to save');
    } finally {
      setLoading(false);
    }
  };
  
  // Selection counts
  const selectedCount = activeTab === 'acceptance' ? selectedAcceptance.size : selectedKPIs.size;
  const totalCount = activeTab === 'acceptance' ? acceptance.length : kpis.length;
  
  // Tier upgrade prompt
  const TierUpgradePrompt = () => (
    <div className="p-4 bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-lg text-center">
      <AlertTriangle className="w-6 h-6 text-amber-500 mx-auto mb-2" />
      <p className="text-sm font-medium text-amber-800 mb-1">
        KPI Suggestions Require Pro Tier
      </p>
      <p className="text-xs text-amber-700 mb-3">
        Upgrade to access AI-powered KPI suggestions and advanced scoping features.
      </p>
      <button 
        className="px-4 py-1.5 text-xs font-medium text-white bg-gradient-to-r from-purple-600 to-indigo-600 rounded-lg hover:from-purple-700 hover:to-indigo-700 transition-all"
        data-testid="upgrade-btn"
      >
        Upgrade to Pro →
      </button>
    </div>
  );
  
  return (
    <div className={`bg-gradient-to-br from-white to-gray-50 rounded-xl border border-gray-200 shadow-sm ${className}`} data-testid="acceptance-kpi-card">
      {/* Header */}
      <div 
        className="flex items-center justify-between p-4 cursor-pointer"
        onClick={() => variant !== 'step' && setExpanded(!expanded)}
        role="button"
        aria-expanded={expanded}
        tabIndex={0}
        onKeyDown={(e) => e.key === 'Enter' && variant !== 'step' && setExpanded(!expanded)}
      >
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center">
            <Sparkles className="w-5 h-5 text-white" />
          </div>
          <div>
            <h3 className="font-semibold text-gray-900">Acceptance & KPIs</h3>
            <p className="text-xs text-gray-500">
              {acceptance.length} criteria · {kpis.length} KPIs
              {hasChanges && <span className="ml-2 text-amber-600">• Unsaved changes</span>}
            </p>
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          {hasChanges && (
            <span className="px-2 py-1 text-xs bg-amber-100 text-amber-700 rounded-full">
              Unsaved
            </span>
          )}
          {variant !== 'step' && (
            expanded ? (
              <ChevronUp className="w-5 h-5 text-gray-400" />
            ) : (
              <ChevronDown className="w-5 h-5 text-gray-400" />
            )
          )}
        </div>
      </div>
      
      {/* Content */}
      {expanded && (
        <div className="px-4 pb-4">
          {/* Helper text for wizard variant */}
          {variant === 'step' && (
            <div className="mb-4 p-3 bg-blue-50 border border-blue-100 rounded-lg flex items-start gap-2">
              <Info className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-blue-700">
                Review and save the Acceptance Criteria and KPIs. You can edit anytime.
              </p>
            </div>
          )}
          
          {/* Tabs */}
          <div className="flex gap-1 p-1 bg-gray-100 rounded-lg mb-4">
            <button
              onClick={() => setActiveTab('acceptance')}
              className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                activeTab === 'acceptance' 
                  ? 'bg-white text-gray-900 shadow-sm' 
                  : 'text-gray-600 hover:text-gray-900'
              }`}
              data-testid="acceptance-tab"
            >
              <CheckCircle className="w-4 h-4" />
              Acceptance ({acceptance.length})
            </button>
            <button
              onClick={() => setActiveTab('kpis')}
              className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                activeTab === 'kpis' 
                  ? 'bg-white text-gray-900 shadow-sm' 
                  : 'text-gray-600 hover:text-gray-900'
              }`}
              data-testid="kpis-tab"
            >
              <Target className="w-4 h-4" />
              KPIs ({kpis.length})
            </button>
          </div>
          
          {/* Bulk Actions Bar */}
          {canEdit && totalCount > 0 && (
            <div className="flex items-center justify-between mb-3 p-2 bg-gray-50 rounded-lg">
              <div className="flex items-center gap-2">
                <button
                  onClick={selectAll}
                  className="text-xs text-purple-600 hover:text-purple-700 font-medium"
                  data-testid="select-all-btn"
                >
                  Select All
                </button>
                {selectedCount > 0 && (
                  <span className="text-xs text-gray-500">
                    {selectedCount} selected
                  </span>
                )}
              </div>
              {selectedCount > 0 && (
                <div className="flex items-center gap-2">
                  <button
                    onClick={acceptAllSelected}
                    className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-green-600 hover:bg-green-50 rounded"
                    data-testid="accept-selected-btn"
                  >
                    <CheckCheck className="w-3 h-3" />
                    Accept
                  </button>
                  <button
                    onClick={discardSelected}
                    className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-red-600 hover:bg-red-50 rounded"
                    data-testid="discard-selected-btn"
                  >
                    <Trash2 className="w-3 h-3" />
                    Discard
                  </button>
                </div>
              )}
            </div>
          )}
          
          {/* Tab Content */}
          <div className="space-y-3">
            {activeTab === 'acceptance' ? (
              <>
                {acceptance.length === 0 ? (
                  <div className="text-center py-8">
                    <CheckCircle className="w-10 h-10 text-gray-300 mx-auto mb-3" />
                    <p className="text-sm text-gray-500 mb-3">
                      No acceptance criteria defined yet
                    </p>
                    {canEdit && demandSummary && (
                      <button
                        onClick={generateAcceptanceSuggestions}
                        disabled={generating}
                        className="inline-flex items-center gap-2 px-4 py-2 bg-purple-600 text-white text-sm rounded-lg hover:bg-purple-700 disabled:opacity-50 transition-colors"
                        data-testid="generate-acceptance-btn"
                      >
                        {generating ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Sparkles className="w-4 h-4" />
                        )}
                        Generate with AI
                      </button>
                    )}
                  </div>
                ) : (
                  <>
                    {acceptance.map(item => (
                      <AcceptanceItem
                        key={item.id}
                        item={item}
                        onStatusChange={handleAcceptanceStatusChange}
                        onRemove={removeAcceptance}
                        canEdit={canEdit}
                        isSelected={selectedAcceptance.has(item.id)}
                        onToggleSelect={toggleAcceptanceSelect}
                      />
                    ))}
                    
                    {/* Add new manually */}
                    {canEdit && (
                      <>
                        {showAddNew ? (
                          <div className="p-3 bg-white border border-gray-200 rounded-lg space-y-3">
                            <textarea
                              value={newItemText}
                              onChange={(e) => setNewItemText(e.target.value)}
                              placeholder="Enter acceptance criterion..."
                              className="w-full p-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                              rows={2}
                              maxLength={140}
                            />
                            <div className="flex items-center justify-between">
                              <select
                                value={newItemType}
                                onChange={(e) => setNewItemType(e.target.value)}
                                className="px-2 py-1 text-xs border border-gray-200 rounded"
                              >
                                <option value="functional">Functional</option>
                                <option value="non-functional">Non-Functional</option>
                                <option value="ops">Ops</option>
                              </select>
                              <div className="flex gap-2">
                                <button
                                  onClick={() => {
                                    setShowAddNew(false);
                                    setNewItemText('');
                                  }}
                                  className="px-3 py-1 text-xs text-gray-600 hover:bg-gray-100 rounded"
                                >
                                  Cancel
                                </button>
                                <button
                                  onClick={addNewItem}
                                  disabled={!newItemText.trim()}
                                  className="px-3 py-1 text-xs bg-purple-600 text-white rounded hover:bg-purple-700 disabled:opacity-50"
                                >
                                  Add
                                </button>
                              </div>
                            </div>
                          </div>
                        ) : (
                          <div className="flex gap-2">
                            <button
                              onClick={() => setShowAddNew(true)}
                              className="flex-1 flex items-center justify-center gap-2 py-2 text-sm text-gray-600 hover:text-gray-800 hover:bg-gray-50 rounded-lg border border-dashed border-gray-300 transition-colors"
                              data-testid="add-manual-btn"
                            >
                              <Plus className="w-4 h-4" />
                              Add Another
                            </button>
                            <button
                              onClick={generateAcceptanceSuggestions}
                              disabled={generating}
                              className="flex-1 flex items-center justify-center gap-2 py-2 text-sm text-purple-600 hover:text-purple-700 hover:bg-purple-50 rounded-lg border border-dashed border-purple-300 transition-colors"
                              data-testid="generate-more-btn"
                            >
                              {generating ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <Sparkles className="w-4 h-4" />
                              )}
                              Generate More
                            </button>
                          </div>
                        )}
                      </>
                    )}
                  </>
                )}
              </>
            ) : (
              <>
                {!canSuggestKPIs ? (
                  <TierUpgradePrompt />
                ) : kpis.length === 0 ? (
                  <div className="text-center py-8">
                    <Target className="w-10 h-10 text-gray-300 mx-auto mb-3" />
                    <p className="text-sm text-gray-500 mb-3">
                      No KPIs defined yet
                    </p>
                    {canEdit && demandSummary && (
                      <button
                        onClick={generateKPISuggestions}
                        disabled={generating}
                        className="inline-flex items-center gap-2 px-4 py-2 bg-purple-600 text-white text-sm rounded-lg hover:bg-purple-700 disabled:opacity-50 transition-colors"
                        data-testid="generate-kpis-btn"
                      >
                        {generating ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Sparkles className="w-4 h-4" />
                        )}
                        Generate with AI
                      </button>
                    )}
                  </div>
                ) : (
                  <>
                    {kpis.map(item => (
                      <KPIItem
                        key={item.id}
                        item={item}
                        onStatusChange={handleKPIStatusChange}
                        onRemove={removeKPI}
                        canEdit={canEdit}
                        isSelected={selectedKPIs.has(item.id)}
                        onToggleSelect={toggleKPISelect}
                      />
                    ))}
                    {canEdit && (
                      <button
                        onClick={generateKPISuggestions}
                        disabled={generating}
                        className="w-full flex items-center justify-center gap-2 py-2 text-sm text-purple-600 hover:text-purple-700 hover:bg-purple-50 rounded-lg border border-dashed border-purple-300 transition-colors"
                      >
                        {generating ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Sparkles className="w-4 h-4" />
                        )}
                        Generate More
                      </button>
                    )}
                  </>
                )}
              </>
            )}
          </div>
          
          {/* Footer caption */}
          <div className="mt-4 pt-3 border-t border-gray-100">
            <p className="text-xs text-gray-500 flex items-center gap-1">
              <Info className="w-3 h-3" />
              Saved items are inherited across scope for governance & checkpoints.
            </p>
          </div>
          
          {/* Save Button */}
          {hasChanges && canEdit && (
            <div className="mt-4">
              <button
                onClick={handleSave}
                disabled={loading}
                className="w-full flex items-center justify-center gap-2 py-2.5 bg-gradient-to-r from-purple-600 to-indigo-600 text-white text-sm font-medium rounded-lg hover:from-purple-700 hover:to-indigo-700 disabled:opacity-50 transition-all"
                data-testid="save-btn"
              >
                {loading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Save className="w-4 h-4" />
                )}
                Save Changes
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AcceptanceKPICard;
